/**
 * CLOUD SAVE, reached from Options. Saves are mirrored to the server under a save code - no
 * account, no password - so this screen has two jobs: show this device's code (hidden until asked
 * for, because whoever reads it owns the save) and take a code from another device. The sync
 * itself belongs to the app; this screen only talks to the `CloudPort` it was handed, and says
 * plainly when there is none.
 */

import './cloud.css';
import { formatCode, isValidCode, normaliseCode } from '../../../platform/cloudSave/saveCode';
import type { Ctx, Screen } from '../context';
import { box, button, el, errorMessage, panel, show, textField } from '../dom';
import { frame } from '../widgets/frame';
import { copyText } from './clipboard';
import type { CloudPort, CloudStateName } from './cloudPort';
import { loadFromCode } from './useCode';

const STATE_LABEL: Readonly<Record<CloudStateName, string>> = {
  off: 'OFF',
  idle: 'IN STEP WITH THE SERVER',
  syncing: 'SENDING',
  offline: 'SERVER NOT ANSWERING',
  'conflict-resolved': 'TWO SAVES SETTLED',
  paused: 'PAUSED UNTIL THE GAME IS STARTED AGAIN',
};

const WARNING = 'ANYONE WITH THIS CODE CAN LOAD AND OVERWRITE THIS SAVE. KEEP IT LIKE A PASSWORD. THERE IS NO'
  + ' ACCOUNT BEHIND IT: IF THE CODE IS LOST, SO IS THE SAVE ON THE SERVER.';
const LOCAL_ONLY = 'SAVES ARE KEPT IN THIS BROWSER ONLY. THIS COPY OF THE GAME HAS NO SAVE SERVER BEHIND IT, SO THERE'
  + ' IS NOTHING TO SET UP AND NOTHING LEAVES THIS DEVICE.';

const COPIED = 'COPIED. PASTE IT SOMEWHERE ONLY YOU CAN READ.';
const SELECTED = 'THE CODE IS SELECTED: PRESS CTRL+C (CMD+C) TO COPY IT.';
const BAD_CODE = 'THAT IS NOT A VALID CODE. CHECK EVERY CHARACTER AND TRY AGAIN.';
const HOW_TO = 'ON THE OTHER DEVICE: OPTIONS, CLOUD SAVE, SHOW CODE. TYPE IT HERE; CASE AND DASHES DO NOT MATTER. THE'
  + ' SAVE UNDER THAT CODE THEN REPLACES THE ONE ON THIS DEVICE, AND BOTH DEVICES SHARE IT FROM THEN ON.';

/** Room for a code typed with a space or a dash between every group, and a little slack. */
const CODE_FIELD_MAX = 40;

const two = (n: number): string => String(n).padStart(2, '0');

/** `2026-09-21 14:05`, this device's clock. */
export function stamp(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** The code with every character but the dashes hidden. */
export function masked(code: string): string {
  return formatCode(code).replace(/[^-]/g, '•');
}

function localOnly(ctx: Ctx): Screen {
  const f = frame('cloud', 'CLOUD SAVE');
  const back = button('BACK', () => ctx.nav.options());
  const line = el('div', 'menu-prose menu-paper', LOCAL_ONLY);
  f.body.append(box('menu-col menu-grow menu-cloud-none', line, box('menu-row', back)));
  return { el: f.el, back: () => ctx.nav.options(), initialFocus: () => back };
}

export function cloudScreen(ctx: Ctx): Screen {
  const given = ctx.cloud;
  if (given === undefined || given.status().state === 'off') return localOnly(ctx);
  const port: CloudPort = given;

  const f = frame('cloud', 'CLOUD SAVE');
  let revealed = false;
  let busy = false;

  /* ---- this device: status and code */
  const device = panel('THIS DEVICE', 'menu-cloud-panel');
  const state = el('span', 'menu-paper');
  const synced = el('span', 'menu-dim menu-small');
  const message = el('div', 'menu-prose menu-small menu-cloud-message');
  message.setAttribute('role', 'status');
  const code = textField('SAVE CODE', '', CODE_FIELD_MAX);
  code.input.readOnly = true;
  code.input.classList.add('menu-cloud-code');
  const said = el('div', 'menu-prose menu-small menu-good menu-hidden');
  said.setAttribute('role', 'status');

  const say = (text: string): void => {
    said.textContent = text;
    show(said, text !== '');
  };
  const current = (): string => port.code() ?? '';

  function render(): void {
    const status = port.status();
    state.textContent = STATE_LABEL[status.state];
    state.classList.toggle('menu-warn', status.state === 'offline' || status.state === 'paused');
    const last = status.lastSyncedAt;
    synced.textContent = last === null ? 'NOTHING SENT YET' : `LAST EXCHANGE ${stamp(last)}`;
    message.textContent = status.message;
    show(message, status.message !== '');
    code.input.value = revealed ? formatCode(current()) : masked(current());
    reveal.textContent = revealed ? 'HIDE CODE' : 'SHOW CODE';
    reveal.setAttribute('aria-pressed', revealed ? 'true' : 'false');
  }

  const reveal = button('SHOW CODE', () => {
    revealed = !revealed;
    say('');
    render();
  }, 'menu-btn-small');
  const copy = button('COPY', () => {
    // The fallback selects what the field shows, so the field has to show the code first.
    revealed = true;
    render();
    void copyText(formatCode(current()), code.input).then((outcome) => {
      say(outcome === 'copied' ? COPIED : SELECTED);
    });
  }, 'menu-btn-small');
  copy.title = 'COPY THE CODE TO THE CLIPBOARD.';
  code.row.append(reveal, copy);
  device.body.append(
    box('menu-row menu-cloud-status', el('span', 'menu-field-label', 'STATUS'), state, synced),
    message,
    code.row,
    said,
    el('div', 'menu-prose menu-small menu-warn menu-cloud-warning', WARNING),
  );

  /* ---- a code from another device */
  const other = panel('USE A CODE FROM ANOTHER DEVICE', 'menu-cloud-panel');
  const typed = textField('THEIR CODE', '', CODE_FIELD_MAX);
  typed.input.classList.add('menu-cloud-code');
  typed.input.placeholder = 'XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XX';
  const error = el('div', 'menu-error menu-hidden');
  error.setAttribute('role', 'alert');
  const refuse = (why: string): void => {
    error.textContent = why === '' ? '' : `! ${why}`;
    show(error, why !== '');
  };
  typed.input.addEventListener('input', () => {
    refuse('');
    // Tidy as it is typed (case, look-alike letters, dashes) - but only with the caret at the end,
    // or fixing a character in the middle would throw the caret to the end on every keystroke.
    const caret = typed.input.selectionStart ?? typed.input.value.length;
    if (caret >= typed.input.value.length) typed.input.value = formatCode(typed.input.value);
  });
  typed.input.addEventListener('blur', () => {
    typed.input.value = formatCode(typed.input.value);
  });

  const setBusy = (on: boolean): void => {
    busy = on;
    load.disabled = on;
    back.disabled = on;
    typed.input.disabled = on;
  };
  const load = button('LOAD THAT SAVE', () => {
    const wanted = normaliseCode(typed.input.value);
    typed.input.value = formatCode(wanted);
    if (!isValidCode(wanted)) {
      // Refused here, by the checksum: a mistyped code never reaches the server.
      refuse(wanted === '' ? 'TYPE THE CODE SHOWN ON THE OTHER DEVICE.' : BAD_CODE);
      typed.input.focus();
      return;
    }
    loadFromCode(ctx, port, wanted, {
      setBusy,
      refuse: (why) => {
        refuse(why);
        typed.input.focus();
      },
    });
  });
  typed.row.append(load);
  other.body.append(
    el('div', 'menu-prose menu-small menu-dim', HOW_TO),
    typed.row,
    error,
  );

  const back = button('BACK', () => ctx.nav.options());
  let unsubscribe: () => void = () => undefined;
  try {
    unsubscribe = port.subscribe(render);
    render();
  } catch (err) {
    refuse(errorMessage(err));
  }

  const panels = box('menu-col menu-grow menu-scroll', device.panel, other.panel);
  f.body.append(panels, box('menu-col menu-cloud-side', back));
  return {
    el: f.el,
    // A save being replaced must not be walked away from: the answer decides whether the game restarts.
    back: () => {
      if (!busy) ctx.nav.options();
    },
    initialFocus: () => back,
    dispose: () => unsubscribe(),
  };
}
